import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { BiKey } from "react-icons/bi";
import { AiOutlineDelete, AiOutlineStop } from "react-icons/ai";
import { MdSettingsBackupRestore } from "react-icons/md";
import toast from "react-hot-toast";
import { ApiGetMe, ApiUpdateEntity } from "@/legacy/ApiCallerOld";
import { useEntity } from "@/legacy/useEntity";
import { useCache } from "@/Hooks/useCache";
import { PushSubscriptionManager } from "@/Fragments/EntityViewer/EntitySpecific/PushSubscriptionManager";
import { useAgent } from "@/Hooks/useAgent";
import { ChangePasswordModal } from "../../../Components/Utility/Modals/ChangePasswordModal";
import { Button, ButtonRed } from "../../../Components/Button/Button";
import OtherFunctions from "../../../Helpers/OtherFunctions";
import { EntityViewerToolbar } from "../EntityViewerToolbar";
import { agentLoggedIn } from "../../../Store/Reducers/loggedInAgentSlice";
import { useAppDispatch } from "../../../Store/hooks";
import { AgentAvatar } from "../../../Components/Avatar/AgentAvatar";
import { useNumberParams } from "../../../Hooks/useNumberParams";
import { getImageUploadHandler } from "../../../Components/FormComponents/HtmlEditor/imageUploadHandler";
import { Tickbox } from "@shared/Components/FormComponents/Tickbox/Tickbox";
import { W_Tickbox } from "@shared/Components/FormComponents/Tickbox/W_Tickbox";
import { FormError } from "@shared/Components/Form/FormError";
import { FormParent } from "@shared/Components/Form/FormParent";
import { FormSection } from "@shared/Components/Form/FormSection";
import { FormReadBox } from "@shared/Components/Form/FormReadBox";
import { FormReadPair } from "@shared/Components/Form/FormReadPair";
import { Agent } from "@shared/Entities/EntityTypes";
import W_TextInput from "@shared/Components/FormComponents/TextInput/W_TextInput";
import { datetimeToWhenString } from "@shared/Helpers/DateFunctions";
import W_HtmlEditor from "@shared/Components/FormComponents/HtmlEditor/W_HtmlEditor";
import { InputTypes } from "@shared/Components/FormComponents/TextInput/InputTypes";
import { Entities } from "@shared/Entities/Entities";
import TextInput from "@shared/Components/FormComponents/TextInput/TextInput";
import { FormHotkeys } from "@shared/Components/Utils/FormHotkeys";

export function AgentViewer() {
	const { id } = useNumberParams();
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const [editmode, setEditmode] = useState<boolean>(id == -1);
	const agent = useAgent();
	const cache = useCache();

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { data, formError, fieldErrors, updateEntity, createEntity, deleteEntity } = useEntity<Agent>(id, Entities.AGENT);
	const { control, handleSubmit, reset, setValue, getValues, setError, watch } = useForm<Agent>({ defaultValues: data });
	const isMe = agent != null && data != null && agent.id == data.id;

	useEffect(() => {
		// Override form data when data changes.
		reset(data);
	}, [data]);

	useEffect(() => {
		// Override errors when they change in props.
		OtherFunctions.setFieldErrors(setError, fieldErrors);
	}, [fieldErrors]);

	async function disableAgent() {
		setValue("disabled", true);

		const resSuccessful = await updateEntity(getValues());

		if (resSuccessful) {
			toast.success("Agent disabled.");
		} else {
			toast.error("Could not disable agent.");
		}
	}

	async function restoreAgent() {
		setValue("disabled", false);

		const resSuccessful = await updateEntity(getValues());

		if (resSuccessful) {
			toast.success("Agent restored.");
		} else {
			toast.error("Could not restore agent.");
		}
	}

	async function deleteAgent() {
		const deleteSuccess = await deleteEntity();

		if (deleteSuccess) {
			navigate("/config/manage/agents");
		}
	}

	function cancelEdit() {
		setEditmode(false);
		reset(data);

		if (id == -1) {
			navigate("/config/manage/agents");
		}
	}

	async function formSubmit(data: Agent) {
		if (data.id > 0) {
			const updateSuccess = await updateEntity(data);

			if (updateSuccess) {
				setEditmode(false);
				refreshLoggedInAgent();
			}
		} else {
			const newId = await createEntity(data);

			if (newId != null && newId > -1) {
				setEditmode(false);
				navigate("/config/manage/agents/" + newId);
			}
		}
	}

	async function refreshLoggedInAgent() {
		const ApiResponse = await ApiGetMe();

		if (ApiResponse.data != null && ApiResponse.data.id != null && ApiResponse.data.id > 0) {
			dispatch(agentLoggedIn(ApiResponse.data));
		}
	}

	function newAvatarCallback(dataBase64: string, mimeType: string) {
		let newData = Object.assign({}, getValues());
		newData = OtherFunctions.SafeChangeProperty(newData, "profilePictureData", dataBase64);
		newData = OtherFunctions.SafeChangeProperty(newData, "profilePictureMimetype", mimeType);

		if (newData.id > 0) {
			updateEntity(newData);
		}
	}

	async function submitNewPassword(password: string) {
		const toastId = toast.loading("Changing password...");

		const agent = getValues();
		agent.password = password;

		const response = await ApiUpdateEntity<Agent>(Entities.AGENT, agent.id, agent);

		if (response.successful) {
			toast.success("Password changed.", { id: toastId });
			reset(response.data);
		} else {
			toast.error("Could not change password. " + response.errorCode + ": " + response.errorMsg, { id: toastId });
		}

		setPasswordModalOpen(false);
	}

	const readButtons = data?.disabled ?
		<>
			<Button className="" onClick={restoreAgent} icon={<MdSettingsBackupRestore />} label="Restore" />
			<ButtonRed className="" label="Delete Forever" icon={<AiOutlineDelete />} onClick={deleteAgent} />
		</>
		:
		<>
			<Button icon={<BiKey />} label="Change password" onClick={() => setPasswordModalOpen(true)} />
			<Button className="" label="Disable" icon={<AiOutlineStop />} onClick={disableAgent} />
		</>;

	const isAdmin = agent?.isAdmin ?? false;
	const canEdit = isAdmin || agent?.id == id;

	return (
		id != null && data != null ?
			<>
				<FormHotkeys onSubmit={handleSubmit(formSubmit)} onCancel={cancelEdit} />

				<form onSubmit={handleSubmit(formSubmit)} className="h-full w-full flex flex-col">
					{!data.deleted &&
						<EntityViewerToolbar
							editmode={editmode}
							isNew={id == -1}
							backUrl="/config/manage/agents"
							onEdit={canEdit ? () => setEditmode(true) : undefined}
							onCancel={cancelEdit}
							extraEditButtons={canEdit ? <Button icon={<BiKey />} label="Change password" onClick={() => setPasswordModalOpen(true)} /> : undefined}
							extraReadButtons={isAdmin ? readButtons : undefined}
						/>}

					{editmode ?
						<div className="overflow-auto">
							<FormParent title={getAgentTitle(data)}>

								<FormError error={formError} />

								<FormSection title="Details">
									<W_TextInput
										control={control}
										dataname="name"
										label="Name"
										mandatory
										autoFocus={id < 0}
									/>
									<W_TextInput
										control={control}
										dataname="email"
										label="Email"
										mandatory
										type={InputTypes.Email}
										autocomplete="off"
									/>

									{id == -1 ?
										<W_TextInput
											control={control}
											dataname="password"
											label="Password"
											type={InputTypes.Password}
											mandatory
											autocomplete="off"
										/>
										: null}

									<W_TextInput control={control} dataname="jobTitle" label="Job Title" />
									<W_TextInput control={control} dataname="phoneNumber" label="Phone number" type={InputTypes.Tel} />
								</FormSection>

								<FormSection title="Avatar">
									<AgentAvatar agent={data} widthPx={70} updateAvatar={newAvatarCallback} hideAgentStatus />
								</FormSection>

								{isAdmin &&
									<FormSection title="Permissions">
										<W_Tickbox control={control} dataname="isAdmin" valueName="isAdmin" label="Administrator" />
									</FormSection>}

								<FormSection title="Email Notifications">
									<Tickbox
										label="A user created a ticket"
										valueName="prefs.enableEmailNotifications"
										value={watch("prefs.enableEmailNotifications") == "true" ? true : false}
										onChange={(_key, value) => {setValue("prefs.enableEmailNotifications", value ? "true" : "false");}}
									/>

									<Tickbox
										label="A ticket was assigned to me"
										valueName="prefs.enableTicketAssignedNotifications"
										value={watch("prefs.enableTicketAssignedNotifications") == "true" ? true : false}
										onChange={(_key, value) => {setValue("prefs.enableTicketAssignedNotifications", value ? "true" : "false");}}
									/>
								</FormSection>

								{isMe &&
									<FormSection
										title="Push Notifications"
										titleTooltip={<span>A push notification will be sent to your device for <b>every</b> new email recieved.</span>}
									>
										<PushSubscriptionManager />
									</FormSection>}


								<FormSection
									title="Greeting"
									titleTooltip="This will form the first line of your email responses. Set to blank to skip this option. You can also use '$name' to pull in the user's name."
								>
									<TextInput
										dataname=""
										label="Greeting"
										value={watch("prefs.emailGreeting") === undefined ? "Hi $name," : watch("prefs.emailGreeting")}
										onChange={(value) => {setValue("prefs.emailGreeting", value);}}
									/>
								</FormSection>

								{id > 0 &&
									<FormSection
										title="Email Signature"
										className="w-[50rem]"
									>
										<W_HtmlEditor
											control={control}
											dataname="signature"
											agentFeatures
											handleCancel={cancelEdit}
											handleSubmit={(note) => {
												setValue("signature", note);
												handleSubmit(formSubmit)();
											}}
											cannedReplies={cache.CannedReplies}
											imageUploadHandler={getImageUploadHandler(cache.getTenantId())}
										/>
									</FormSection>}
							</FormParent>
						</div>
						:
						<div className="overflow-auto">
							<AgentDisplay agent={data} />
						</div>}
				</form>

				<ChangePasswordModal updatePassword={submitNewPassword} isOpen={passwordModalOpen} closeModal={() => setPasswordModalOpen(false)} />
			</>
			:
			null
	);
}

function getAgentTitle(agent: Agent) {
	return (agent.name ?? "") + (agent.deleted ? " (Deleted)" : agent.disabled ? " (Disabled)" : "");
}

interface AgentDisplayProps {
	agent: Agent;
}

function AgentDisplay(props: AgentDisplayProps) {
	const agent = useAgent();
	const [subscribed, setSubscribed] = useState(false);

	useEffect(() => {
		firstUpdated();

		async function firstUpdated() {
			const registration = await navigator.serviceWorker.getRegistration();
			setSubscribed(Boolean(await registration?.pushManager?.getSubscription()));
		}
	}, []);

	const isMe = agent?.id == props.agent.id;

	return (
		<FormParent title={getAgentTitle(props.agent)}>
			<FormSection title="Details">
				<FormReadBox>
					<FormReadPair
						name="Name"
						value={props.agent.name}
					/>
					<FormReadPair
						name="Email"
						value={props.agent.email}
					/>
					<FormReadPair
						name="Job Title"
						value={props.agent.jobTitle}
					/>
					<FormReadPair
						name="Phone Number"
						value={props.agent.phoneNumber}
					/>
				</FormReadBox>
			</FormSection>

			<FormSection title="Avatar">
				<AgentAvatar agent={props.agent} widthPx={70} hideAgentStatus />
			</FormSection>

			<FormSection title="Permissions">
				<FormReadBox>
					<FormReadPair
						name="Administrator"
						value={props.agent.isAdmin ? "True" : "False"}
					/>
				</FormReadBox>
			</FormSection>

			<FormSection title="Email Notifications">
				<FormReadBox>
					<FormReadPair
						name="A user created a ticket"
						value={props.agent.prefs.enableEmailNotifications == "true" ? "True" : "False"}
					/>
					<FormReadPair
						name="A ticket was assigned to me"
						value={props.agent.prefs.enableTicketAssignedNotifications == "true" ? "True" : "False"}
					/>
				</FormReadBox>
			</FormSection>

			{isMe &&
				<FormSection
					title="Push Notifications"
					titleTooltip={<span>A push notification will be sent to your device for <b>every</b> new email recieved.</span>}
				>
					<FormReadBox>
						<FormReadPair
							name="Enabled"
							value={subscribed ? "True" : "False"}
						/>
					</FormReadBox>
				</FormSection>}

			<FormSection
				title="Greeting"
				titleTooltip={<span>This will form the first line of your email responses. Set to blank to skip this option. You can also use '$name' to pull in the user's name.</span>}
			>
				<FormReadBox>
					<FormReadPair
						name="Greeting"
						value={props.agent.prefs.emailGreeting === undefined ? "Hi $name," : props.agent.prefs.emailGreeting === "" ? "-" : props.agent.prefs.emailGreeting}
					/>
				</FormReadBox>
			</FormSection>

			<FormSection title="Email Signature">
				{props.agent.signature ?
					<div
						className="text-base p-3 bg-white text-gray-700 rounded border border-gray-200"
						dangerouslySetInnerHTML={{ __html: props.agent.signature }}
					>
					</div>
					:
					<div className="font-semibold text-slate-500">
						-
					</div>}
			</FormSection>

			<FormSection>
				<FormReadBox>
					<FormReadPair
						name="Updated"
						value={datetimeToWhenString(props.agent.modified)}
					/>
					<FormReadPair
						name="Created"
						value={datetimeToWhenString(props.agent.created)}
					/>
				</FormReadBox>
			</FormSection>
		</FormParent>
	);
}
